import { useParams } from 'react-router-dom';
import { Link } from 'react-router-dom';
import { comics, categories } from '@/mocks/comics';
import ComicCard from '@/components/feature/ComicCard';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import Breadcrumb from '@/components/feature/Breadcrumb';
import PageSEO from '@/components/feature/PageSEO';

const categoryNames: Record<string, string> = {
  rexue: '热血',
  lianai: '恋爱',
  xuanyi: '悬疑',
  qihuan: '奇幻',
  xiaoyuan: '校园',
  kehuan: '科幻',
  kongbu: '恐怖',
  gaoxiao: '搞笑',
};

const categoryDescriptions: Record<string, string> = {
  rexue: '热血漫画专区汇集了最燃最爽的韩国热血漫画，充满战斗与成长的热血故事，让你感受青春与激情的碰撞。',
  lianai: '恋爱漫画专区为你带来最甜蜜的韩国恋爱漫画，从校园初恋到都市爱情，每一个故事都让人心动不已。',
  xuanyi: '悬疑漫画专区精选韩国悬疑推理漫画，烧脑的剧情、出人意料的转折，带你走进一个又一个神秘世界。',
  qihuan: '奇幻漫画专区收录最精彩的韩国奇幻漫画，魔法、神兽、异世界冒险，无限想象等你探索。',
  xiaoyuan: '校园漫画专区提供最好看的韩国校园漫画，青春、友情、成长，重温那段最美好的校园时光。',
  kehuan: '科幻漫画专区带来最具想象力的韩国科幻漫画，未来世界、太空冒险、人工智能，开启科幻之旅。',
  kongbu: '恐怖漫画专区汇集最惊悚的韩国恐怖漫画，胆小慎入！每一个故事都能让你背后发凉。',
  gaoxiao: '搞笑漫画专区提供最欢乐的韩国搞笑漫画，开心一笑，治愈一天的疲惫。',
};

export default function CategoryPage() {
  const { slug } = useParams<{ slug: string }>();
  const categoryName = categoryNames[slug || ''] || '全部分类';
  const categoryDesc = categoryDescriptions[slug || ''] || '浏览全部韩国漫画分类，找到你喜欢的作品类型。';
  const filteredComics = slug ? comics.filter((c) => c.category === categoryName) : comics;

  const top3 = filteredComics.slice(0, 3);

  const pageSchema = {
    '@context': 'https://schema.org',
    '@type': 'CollectionPage',
    name: `${categoryName}漫画 - 韩漫网`,
    description: categoryDesc,
    url: `http://stgoldcoast.com/category/${slug}`,
    isPartOf: {
      '@type': 'WebSite',
      name: '韩漫网',
      url: 'http://stgoldcoast.com/',
    },
  };

  return (
    <>
      <PageSEO
        title={`${categoryName}漫画_免费韩国${categoryName}漫画大全_下拉式在线观看 - 韩漫网`}
        description={categoryDesc}
        keywords={`韩漫网,韩国漫画,${categoryName}漫画,免费韩漫,下拉式阅读`}
        canonicalUrl={`/category/${slug}`}
        schemaJson={pageSchema}
      />

      <Navbar />

      <main>
        {/* Hero */}
        <section className="relative w-full h-[380px] md:h-[480px] flex flex-col items-center justify-center overflow-hidden">
          <img
            src="https://readdy.ai/api/search-image?query=A%20wide%20Korean%20manhwa%20style%20illustration%20with%20warm%20coral%20and%20golden%20tones%2C%20abstract%20comic%20panel%20layout%2C%20dynamic%20action%20lines%2C%20clean%20modern%20composition%2C%20soft%20gradient%20background%2C%20cinematic%20lighting%2C%20digital%20art%20style%2C%20vibrant%20colors&width=1600&height=800&seq=category-hero-bg&orientation=landscape"
            alt={`${categoryName}漫画专区`}
            title={`${categoryName}韩国漫画 - 韩漫网`}
            className="absolute inset-0 w-full h-full object-cover object-top"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-black/40 via-black/25 to-black/45" />

          <div className="relative z-10 w-full flex flex-col items-center text-center px-4">
            <h1 className="font-heading text-3xl md:text-5xl font-bold text-background-50 mb-3">
              {categoryName}漫画
            </h1>
            <p className="text-base md:text-lg text-background-50/70 max-w-lg leading-relaxed">
              {categoryDesc}
            </p>
          </div>
        </section>

        <div className="w-full max-w-[1280px] mx-auto px-4 md:px-6 py-6">
          <Breadcrumb
            items={[
              { label: '首页', href: '/' },
              { label: '全部分类', href: '/category/rexue' },
              { label: `${categoryName}漫画` },
            ]}
          />
        </div>

        {/* TOP 3 */}
        <section className="py-10 md:py-14 bg-background-50">
          <div className="w-full max-w-[1280px] mx-auto px-4 md:px-6">
            <div className="mb-8">
              <h2 className="font-heading text-3xl md:text-4xl font-light text-foreground-900">
                {categoryName} TOP 3
              </h2>
              <p className="text-sm text-foreground-500 mt-2">该分类下最受欢迎的韩国漫画</p>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5 md:gap-6">
              {top3.map((comic, i) => (
                <Link
                  key={comic.id}
                  to={`/comic/${comic.id}`}
                  className="group flex flex-col rounded-2xl overflow-hidden bg-background-50 border border-background-200/70 hover:border-primary-200 transition-all cursor-pointer"
                  data-product-shop=""
                >
                  <div className={`h-52 overflow-hidden ${i === 0 ? 'bg-accent-100' : 'bg-background-200'}`}>
                    <img
                      src={comic.cover}
                      alt={comic.title}
                      title={`${comic.title} - ${categoryName}韩国漫画`}
                      className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-500"
                      loading="lazy"
                    />
                  </div>
                  <div className="p-5 flex flex-col items-center text-center">
                    <h3 className="font-semibold text-foreground-900 mb-1">{comic.title}</h3>
                    <p className="text-2xl font-bold text-foreground-800 mb-3">{comic.rating}<span className="text-xs font-normal text-foreground-400 ml-1">分</span></p>
                    <span
                      className={`inline-flex items-center justify-center px-6 py-2.5 rounded-full text-sm font-medium whitespace-nowrap transition-all ${
                        i === 0
                          ? 'bg-primary-500 text-background-50 hover:bg-primary-600'
                          : 'border border-foreground-300 text-foreground-700 hover:bg-foreground-50'
                      }`}
                    >
                      开始阅读
                    </span>
                    <p className="text-[11px] text-foreground-400 mt-3">已更新至第{comic.chapters}话</p>
                  </div>
                </Link>
              ))}
            </div>
          </div>
        </section>

        {/* All comics in category */}
        <section className="py-10 md:py-14 bg-background-50">
          <div className="w-full max-w-[1280px] mx-auto px-4 md:px-6">
            <h2 className="font-heading text-2xl font-bold text-foreground-900 mb-8">
              全部{categoryName}<span className="text-primary-500">漫画</span>
              <span className="text-sm font-normal text-foreground-500 ml-2">共{filteredComics.length}部</span>
            </h2>

            {filteredComics.length > 0 ? (
              <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 xl:grid-cols-6 gap-4 md:gap-5">
                {filteredComics.map((comic) => (
                  <ComicCard
                    key={comic.id}
                    id={comic.id}
                    slug={comic.slug}
                    title={comic.title}
                    author={comic.author}
                    category={comic.category}
                    rating={comic.rating}
                    cover={comic.cover}
                    chapters={comic.chapters}
                    views={comic.views}
                    isHot={comic.isHot}
                  />
                ))}
              </div>
            ) : (
              <div className="text-center py-20">
                <div className="w-16 h-16 mx-auto mb-4 rounded-2xl bg-background-200 flex items-center justify-center">
                  <i className="ri-book-open-line text-2xl text-foreground-400" />
                </div>
                <p className="text-foreground-500">该分类暂无漫画，请浏览其他分类</p>
                <Link
                  to="/category/rexue"
                  className="inline-flex items-center gap-1 mt-4 text-sm text-primary-500 hover:text-primary-600 transition-colors cursor-pointer"
                >
                  查看热门分类
                  <i className="ri-arrow-right-line w-4 h-4 flex items-center justify-center" />
                </Link>
              </div>
            )}
          </div>
        </section>

        {/* Other categories */}
        <section className="py-10 md:py-14 bg-background-100">
          <div className="w-full max-w-[1280px] mx-auto px-4 md:px-6">
            <h2 className="font-heading text-2xl font-bold text-foreground-900 mb-6">其他分类</h2>
            <div className="flex flex-wrap gap-3">
              {categories
                .filter((c) => c.slug !== slug)
                .map((cat) => (
                  <Link
                    key={cat.slug}
                    to={`/category/${cat.slug}`}
                    className="inline-flex items-center gap-2 px-4 py-2 bg-secondary-100 text-secondary-900 text-sm rounded-full hover:bg-secondary-200 transition-colors cursor-pointer whitespace-nowrap"
                  >
                    <i className={`${cat.icon} w-4 h-4 flex items-center justify-center`} />
                    {cat.name}
                    <span className="text-secondary-500">({cat.count})</span>
                  </Link>
                ))}
            </div>
          </div>
        </section>
      </main>

      <Footer />
    </>
  );
}